import { atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';
import {
  ICompanyFundingsDataModel,
  IFundingDataModel,
  roundTrackerMetricToFundingModel,
} from '../../../data-models/company-fundings.data-model';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { IInvestmentDataModel } from '../../../data-models/investment.data-model';
import { fetchCompanyFundings } from '../../../services/queries/MaggieCompanyQueries';
import {
  fetchRoundsForCompany,
  fetchTransactionsByCompany2,
} from '../../../services/queries/MaggieTransactionQueries';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { roundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { getCompanySyncIfResolved } from '../../../services/state/CompanyStateJ';
import { fundsByDateDescending } from '../Summary/components/FinancingHistory/FinancingHistoryTable';
import { roundTrackerMetricsByCompanyIdJ } from '../../PortfolioOverview/state/RoundTrackerMetricsState';
import { calculateFundingMetrics } from '../utils/funding-calculations';

export const companyFundingsState = atom<Map<number, ICompanyFundingsDataModel | null>>(
  new Map<number, ICompanyFundingsDataModel | null>()
);

export const CompanyFundingState = atomFamily((companyId: number) =>
  atom<Promise<ICompanyFundingsDataModel | null>>(async (get) => {
    if (companyId <= 0) {
      return null;
    }
    const companyFundings = get(companyFundingsState);
    try {
      return await getOrFetchCompanyFunding(companyFundings, companyId);
    } catch {
      return null;
    }
  })
);

export const CompanyFundingStateLoadable = atomFamily((companyId: number) =>
  loadable(CompanyFundingState(companyId))
);

export type TFinanceData = Partial<Pick<IInvestmentDataModel, 'fmv' | 'moic' | 'xirr'>> &
  IFundingDataModel & { investmentAmount?: number };

// FIXME: Remove when MAGGIE-3559 data hierarchy work is completed.
type ICompanyFinancingHistoryStateKey = { companyId: number; isPortfolioCompany: boolean };

export const CompanyFinancingHistoryState = atomFamily(
  ({ companyId, isPortfolioCompany }: ICompanyFinancingHistoryStateKey) =>
    atom<Promise<TFinanceData[]>>(async (get) => {
      let rawFundings: TFinanceData[];

      if (isPortfolioCompany) {
        let data: IInvestmentDataModel[] | undefined;
        try {
          data = await get(roundTrackerMetricsByCompanyIdJ({ companyId }));
        } catch {
          data = undefined;
        }
        const roundsById = get(roundsByIdMapAtom);

        rawFundings = data?.map((r) => roundTrackerMetricToFundingModel(r, roundsById)) ?? [];
      } else {
        const data = await get(CompanyFundingState(companyId));
        rawFundings = data?.fundings ?? [];
      }

      // Sort by date ascending for cumulative calculation
      const sortedAscending = [...rawFundings].sort((a, b) => {
        const dateA = new Date(a.dealDate).getTime();
        const dateB = new Date(b.dealDate).getTime();
        return dateA - dateB;
      });

      const fundingsWithCalculations = calculateFundingMetrics(sortedAscending);

      return fundingsWithCalculations.sort(fundsByDateDescending);
    }),
  (a, b) => a.companyId === b.companyId && a.isPortfolioCompany === b.isPortfolioCompany
);

async function getOrFetchCompanyFunding(
  companyFunding: Map<number, ICompanyFundingsDataModel | null>,
  companyId: number
): Promise<ICompanyFundingsDataModel | null> {
  if (companyFunding.get(companyId) === undefined) {
    const fundingData = await fetchCompanyFundings(companyId);
    companyFunding.set(companyId, fundingData);

    return fundingData;
  }

  return companyFunding.get(companyId) ?? null;
}

export const latestFundingsByCompanyState = atomFamily((companyId: number) =>
  atom<Promise<TFinanceData | null>>(async (get) => {
    const company = getCompanySyncIfResolved(companyId);
    const fundings = await get(
      CompanyFinancingHistoryState({ companyId, isPortfolioCompany: company?.companyType === 'portfolio' })
    );
    return (
      [...fundings].sort(fundsByDateDescending).find((funding) => funding.valuationInMillions != null) ?? null
    );
  })
);

export const CompanyFinanceRoundsState = atomFamily((companyId: number) =>
  atom<Promise<IFinanceRoundDataModel[]>>(async () => {
    if (companyId <= 0) {
      return [];
    }
    try {
      return await fetchRoundsForCompany(companyId);
    } catch (err) {
      console.error('Failed to fetch finance rounds:', err);
      return [];
    }
  })
);

/** All transactions for a company (raw rows; callers resolve fund/type via id-maps). */
export const CompanyTransactionsState = atomFamily((companyId: number) =>
  atom<Promise<ITransactionDataModel[]>>(async () => {
    if (companyId <= 0) {
      return [];
    }
    try {
      return await fetchTransactionsByCompany2(companyId);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      return [];
    }
  })
);
